import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, AlertCircle, ExternalLink, FileX } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  borderPrimary,
  darkOrangeTextColor,
  extraSmallTextSize,
  lightAmberBG,
  primaryText,
  roundedLG,
  semiBoldText,
  smallTextSize,
  tertiaryText,
  xxLargeTextSize,
} from '../../../styling/stylingHelper';

import { BatchFileResult, OrderData, SavedOrderRef } from '../../../types/invoiceTypes';
import { Platform } from '../../../types/Platforms';
import { InvoiceDataComponent } from './InvoiceDataComponent';
import { SupplierLogo } from '../../ui/SupplierLogo';
import { MobileCardField } from '../../shared/mobile/ResponsiveTable';
import { getOrderVatAmount, getOrderVatRate, VatRateBadges } from '../../orderhistory/vatUtils';
import { currencyFormatter } from '../../../services/helperService';
import { getTableFormattedDate } from '../../../utils/dateUtils';

// Mobile-only: render this many order cards before showing "Load more" (keeps the
// phone DOM small; desktop always renders the full list — see the render below).
const MOBILE_ORDERS_STEP = 20;

interface OrderListDisplayProps {
  orders: OrderData[];
  fileResults?: BatchFileResult[];
  /** Flat across every file — see {@link SavedOrderRef}. Absent when the batch stored nothing. */
  savedOrders?: SavedOrderRef[];
}

export const OrderListDisplay: React.FC<OrderListDisplayProps> = ({
  orders,
  fileResults,
  savedOrders
}) => {
  const { t } = useTranslation();
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());
  // Mobile "Load more" window (see MOBILE_ORDERS_STEP); reset when the batch changes.
  const [mobileVisibleCount, setMobileVisibleCount] = useState<number>(MOBILE_ORDERS_STEP);

  useEffect(() => {
    setMobileVisibleCount(MOBILE_ORDERS_STEP);
  }, [orders.length]);

  // `orders` is every file's orders concatenated in file order (getAllOrdersFromBatch flattens
  // `results`), so one entry per order in the same sequence maps a card back to its file.
  const orderMeta = useMemo(() => {
    const meta: Array<{ fileIndex: number; filename: string }> = [];
    for (const fileResult of fileResults ?? []) {
      for (let i = 0; i < fileResult.orders.length; i++) {
        meta.push({ fileIndex: fileResult.fileIndex, filename: fileResult.filename });
      }
    }
    return meta;
  }, [fileResults]);

  // savedOrders is flat, so bucket it by file before matching an order inside a file.
  const refsByFile = useMemo(() => {
    const byFile = new Map<number, SavedOrderRef[]>();
    for (const ref of savedOrders ?? []) {
      const existing = byFile.get(ref.fileIndex);
      if (existing) existing.push(ref);
      else byFile.set(ref.fileIndex, [ref]);
    }
    return byFile;
  }, [savedOrders]);

  /**
   * The stored row for one extracted order. Falls back to the `<number>_<yymm>` key a supplier that
   * recycles its order numbers gets stored under, which is why this cannot be a plain map lookup.
   */
  const findSavedRef = (fileIndex: number | undefined, orderNumber: string): SavedOrderRef | undefined => {
    if (fileIndex === undefined) return undefined;
    const refs = refsByFile.get(fileIndex);
    if (!refs) return undefined;
    return refs.find(r => r.orderNumber === orderNumber)
      ?? refs.find(r => r.orderNumber.startsWith(`${orderNumber}_`));
  };

  const toggleOrderExpansion = (index: number) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  if (!orders || orders.length === 0) {
    return (
      <div className="text-center py-8 text-muted dark:text-muted-dark">
        {t('invoices.noOrdersToDisplay')}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h4 className={cn(primaryText, semiBoldText, "mb-3", xxLargeTextSize)}>
        {t('invoices.extractedOrders', { count: orders.length })}
      </h4>

      {/* Show failed files if provided */}
      {fileResults && fileResults.some(r => !r.success) && (
        <div className="space-y-2 mb-4">
          {fileResults.filter(r => !r.success).map((fileResult, idx) => (
            <div
              key={idx}
              className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800"
            >
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                <div>
                  <div className={cn(primaryText, semiBoldText, "text-sm text-red-800 dark:text-red-300")}>
                    {t('invoices.failedFile', { filename: fileResult.filename })}
                  </div>
                  <div className={cn(primaryText, "text-xs text-red-700 dark:text-red-400 mt-0.5")}>
                    {fileResult.error}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Order list — one card renderer shared by the desktop (full) and
          mobile (windowed) branches below. */}
      {(() => {
      const renderOrderCard = (order: OrderData, orderIdx: number) => {
        const isExpanded = expandedOrders.has(orderIdx);

        const meta = orderMeta[orderIdx];
        const filename = meta?.filename;
        const savedRef = findSavedRef(meta?.fileIndex, order.orderNumber);

        // Money figures follow the orders page: VAT via getOrderVatAmount (naive incl−excl is wrong
        // on the shipping-included platforms) and the incl-VAT fallback for a nullable totalWithTaxes.
        const hasTaxData = order.totalWithTaxes !== undefined;
        const totalExVat = order.total ?? 0;
        const totalInclVat = order.totalWithTaxes ?? totalExVat;
        const vatAmount = hasTaxData
          ? getOrderVatAmount(order.platform, order.totalWithTaxes ?? 0, order.total ?? 0, order.shipping ?? 0)
          : 0;

        // Prefer the rates the supplier charged per line; only a line-less order falls back to the
        // order-level (blended) rate.
        const lineVatRates = (order.productData || []).map(p => p.taxRate).filter(rate => rate !== undefined);
        const vatRates = lineVatRates.length > 0
          ? lineVatRates
          : [hasTaxData
              ? getOrderVatRate(order.platform, order.totalWithTaxes ?? 0, order.total ?? 0, order.shipping ?? 0, order.productData)
              : undefined];

        const formattedDate = order.orderDate
          ? getTableFormattedDate(new Date(order.orderDate))
          : t('invoices.notAvailable');

        const supplierName = (
          order.platform === Platform.ANY_INVOICE ? (
            <div className={cn(primaryText, semiBoldText, "text-sm truncate")}>
              {order.unknownSupplierName || t('invoices.unknownSupplier')}
            </div>
          ) : (
            <SupplierLogo
              platform={order.platform}
              unknownSupplierName={order.unknownSupplierName}
              size="large"
              alt={order.platform}
            />
          )
        );

        const duplicatePill = savedRef?.status === 'duplicate' ? (
          <span className={cn(extraSmallTextSize, semiBoldText, lightAmberBG, darkOrangeTextColor, roundedLG, 'px-2 py-0.5 whitespace-nowrap')}>
            {t('invoices.duplicate', { defaultValue: 'Duplicate' })}
          </span>
        ) : null;

        const fileLink = savedRef?.sourceFileUrl ? (
          <a
            href={savedRef.sourceFileUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 flex-shrink-0 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            {t('orderHistory.viewFile')}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        ) : (
          <div
            className="flex-shrink-0 p-2"
            title={t('orderHistory.noFile', { defaultValue: 'No file' })}
          >
            <FileX className="w-4 h-4 opacity-30 text-textColor dark:text-textColor-dark" />
          </div>
        );

        return (
          <div
            key={orderIdx}
            className="bg-background dark:bg-background-dark rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            {/* Order Summary - Clickable. A div rather than a <button> because the file link lives
                inside it, and an anchor may not be nested in a button. */}
            <div
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              onClick={() => toggleOrderExpansion(orderIdx)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleOrderExpansion(orderIdx);
                }
              }}
              className="w-full px-4 py-3 flex items-center justify-between gap-2 hover:bg-backgroundLight dark:hover:bg-backgroundLight-dark transition-colors text-left cursor-pointer"
            >
              {/* Desktop: one row of columns, matching the orders page. */}
              <div className="hidden sm:grid flex-1 min-w-0 grid-cols-6 gap-4 items-center">
                <div className="min-w-0">
                  <div className={cn(primaryText, "text-xs opacity-60 mb-0.5")}>
                    {t('orderHistory.supplierTableHeader')}
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    {supplierName}
                    {duplicatePill}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className={cn(primaryText, "text-xs opacity-60 mb-0.5")}>
                    {t('orderHistory.date')}
                  </div>
                  <div className={cn(primaryText, semiBoldText, "text-sm truncate")}>
                    {formattedDate}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className={cn(primaryText, "text-xs opacity-60 mb-0.5")}>
                    {t('orderHistory.exclVat')}
                  </div>
                  <div className={cn(primaryText, semiBoldText, "text-sm truncate")}>
                    {currencyFormatter.format(totalExVat)}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className={cn(primaryText, "text-xs opacity-60 mb-0.5")}>
                    {t('orderHistory.vatRate')}
                  </div>
                  <VatRateBadges rates={vatRates} taxCalculationMethod={order.taxCalculationMethod} />
                </div>
                <div className="min-w-0">
                  <div className={cn(primaryText, "text-xs opacity-60 mb-0.5")}>
                    {t('orderHistory.vat')}
                  </div>
                  <div className={cn(primaryText, semiBoldText, "text-sm truncate")}>
                    {currencyFormatter.format(vatAmount)}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className={cn(primaryText, "text-xs opacity-60 mb-0.5")}>
                    {t('orderHistory.inclVat')}
                  </div>
                  <div className={cn(primaryText, semiBoldText, "text-sm truncate")}>
                    {currencyFormatter.format(totalInclVat)}
                  </div>
                </div>
              </div>

              {/* Mobile: supplier + incl VAT on top, the VAT breakdown underneath. */}
              <div className="sm:hidden flex-1 min-w-0 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {supplierName}
                    {duplicatePill}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={cn('text-xs font-medium', tertiaryText)}>{t('orderHistory.inclVat')}</span>
                    <span className={cn('text-base font-bold', primaryText)}>{currencyFormatter.format(totalInclVat)}</span>
                  </div>
                </div>
                {/* Two rows, not one: four fields across a 320px card truncates the amounts to
                    "€1.23…", and a money figure that reads wrong is worse than a taller card. */}
                <div className="flex items-stretch border-t border-border dark:border-border-dark pt-3">
                  <MobileCardField label={t('orderHistory.date')} value={formattedDate} divider />
                  <MobileCardField label={t('orderHistory.exclVat')} value={currencyFormatter.format(totalExVat)} />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <MobileCardField label={t('orderHistory.vat')} value={currencyFormatter.format(vatAmount)} />
                  <MobileCardField
                    label={t('orderHistory.vatRate')}
                    value={<VatRateBadges rates={vatRates} taxCalculationMethod={order.taxCalculationMethod} />}
                    align="right"
                  />
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                {fileLink}
                {isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-gray-400 dark:text-textQuaternary-dark" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400 dark:text-textQuaternary-dark" />
                )}
              </div>
            </div>

            {/* Expanded Order Details */}
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700">
                <div className="pt-4">
                  {filename && (
                    <div className={cn(primaryText, "text-xs opacity-60 mb-2")}>
                      {t('orderHistory.file')}: {filename}
                    </div>
                  )}
                  <InvoiceDataComponent
                    orderData={[order]}
                    platform={order.platform}
                  />
                </div>
              </div>
            )}
          </div>
        );
      };

      return (
        <>
          {/* Desktop: full list, unchanged. */}
          <div className="hidden sm:block space-y-2">
            {orders.map((order, orderIdx) => renderOrderCard(order, orderIdx))}
          </div>
          {/* Mobile: truly windowed — only the visible slice is mounted. */}
          <div className="sm:hidden space-y-2">
            {orders.slice(0, mobileVisibleCount).map((order, orderIdx) => renderOrderCard(order, orderIdx))}
            {mobileVisibleCount < orders.length && (
              <button
                type="button"
                onClick={() => setMobileVisibleCount((c) => c + MOBILE_ORDERS_STEP)}
                className={cn(smallTextSize, semiBoldText, 'w-full py-2 rounded-lg border', borderPrimary, primaryText)}
              >
                {t('common.loadMore')}
              </button>
            )}
          </div>
        </>
      );
      })()}
    </div>
  );
};
